<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow v-if="isEffContractVisibility">
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="incalculation.commonWork.isEffContract"
          label="Работа входит в эффективный контракт"
          hide-details
          dense
          :disabled="isShowMode && !canChangeIsEffContractInShowMode"
          @change="updateIncalculation"
        />
      </CardRow>

      <CardAutocompleteRow
        v-model="incalculation.commonWork.year"
        :items="yearList"
        item-value="id"
        item-text="year"
        label="Год"
        placeholder="Выберите год"
        :no-filter="false"
        :disabled="isShowMode"
        :error-messages="errorMessages.commonWork.year"
        :required="!isShowMode"
        @input="
          $v.incalculation.commonWork.year.$touch()
          updateIncalculation()
        "
      />

      <CardTextAreaRow
        v-model="incalculation.fullBibliographic"
        label="Полное библиографическое описание по ГОСТ"
        placeholder="Добавьте библиографическое описание"
        :disabled="isShowMode"
        :error-messages="errorMessages.fullBibliographic"
        :required="!isShowMode"
        @input="
          $v.incalculation.fullBibliographic.$touch()
          updateIncalculation()
        "
      />

      <div
        v-if="!isShowMode"
        class="d-flex"
      >
        <UIAlert title="Необходимо добавить ссылку и/или прикрепить подтверждающий документ" />
      </div>

      <CardFieldRow
        v-model="incalculation.link"
        label="Ссылка"
        placeholder="Добавьте ссылку"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.link"
        :required="!isShowMode"
        @input="
          $v.incalculation.link.$touch()
          updateIncalculation()
        "
      />

      <CardRow
        label="Подтверждающие материалы"
        class="mt-0"
      >
        <UIUploadFile
          acceptFileFormats=".pdf, .jpeg, .jpg, .doc, .docx"
          button-text="Добавить подтверждающие материалы"
          :maxFileWeightInMb="5"
          :is-show-mode="isShowMode"
          :multiple="true"
          :maxFiles="10"
          :fileList="linkFileList"
          @uploadFiles="uploadFiles"
          @removeFile="removeFile"
        />
      </CardRow>

      <h4 class="my-4 mt-10">НИР</h4>

      <CardAutocompleteRow
        v-model="incalculation.inculcationLevel"
        :items="inculcationLevelList"
        item-value="id"
        item-text="name"
        label="Уровень внедрения"
        placeholder="Выберите уровень внедрения"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.inculcationLevel"
        @input="
          $v.incalculation.inculcationLevel.$touch()
          updateIncalculation()
        "
      />

      <CardFieldRow
        v-model="incalculation.inculcationArea"
        label="Область внедрения"
        placeholder="Добавьте область внедрения"
        :disabled="isShowMode"
        :error-messages="errorMessages.inculcationArea"
        :required="!isShowMode"
        @input="
          $v.incalculation.inculcationArea.$touch()
          updateIncalculation()
        "
      />

      <CardFieldRow
        v-model="incalculation.inculcationAct"
        type="number"
        label="Номер акта внедрения"
        placeholder="Добавьте номер акта внедрения"
        :disabled="isShowMode"
        :error-messages="errorMessages.inculcationAct"
        @input="
          $v.incalculation.inculcationAct.$touch()
          updateIncalculation()
        "
      />

      <CardDateRow
        v-model="incalculation.inculcationDate"
        label="Дата акта внедрения"
        placeholder="Добавьте дату"
        :disabled="isShowMode"
        @input="updateIncalculation()"
      />

      <CardFieldRow
        v-model="incalculation.inculcationPlace"
        label="Где внедрено"
        placeholder="Где внедрено"
        :disabled="isShowMode"
        :error-messages="errorMessages.inculcationPlace"
        @input="
          $v.incalculation.inculcationPlace.$touch()
          updateIncalculation()
        "
      />

      <h4 class="my-4 mt-10">Совместно с какими подразделениями Университета подготовлено внедрение</h4>

      <CardAutocompleteRow
        v-model="incalculation.commonWork.departments"
        :items="departmentList"
        item-value="id"
        item-text="name"
        label="Кафедра"
        placeholder="Выберите кафедры"
        multiple
        :disabled="isShowMode"
        :no-filter="false"
        @input="updateIncalculation()"
      />

      <CardTextAreaRow
        v-model="incalculation.remark"
        label="Примечание"
        placeholder="Добавьте примечание"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.remark"
        @input="
          $v.incalculation.remark.$touch()
          updateIncalculation()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Incalculation } from '@/models'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '../../ui/row/CardFieldRow'
import { setValidateMessages } from '../../../scripts/validation'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import CardTextAreaRow from '../../ui/row/CardTextAreaRow'
import CardDateRow from '../../ui/row/CardDateRow'
import { mapGetters } from 'vuex'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'
import UIAlert from '@/components/ui/UIAlert.vue'
import { isEffContractMixin } from '@/mixins/isEffContract'

export default {
  components: { UIAlert, UIUploadFile, CardDateRow, CardTextAreaRow, CardAutocompleteRow, CardFieldRow, CardRow },
  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String,
    linkFileList: Array
  },

  mixins: [isEffContractMixin],

  data: () => ({
    incalculation: new Incalculation()
  }),

  validations() {
    return {
      incalculation: {
        commonWork: {
          year: { required }
        },
        fullBibliographic: { required, maxLength: maxLength(700) },
        inculcationLevel: { required },
        inculcationArea: {
          required,
          maxLength: maxLength(300)
        },
        inculcationAct: { maxLength: maxLength(20) },
        inculcationPlace: { maxLength: maxLength(300) },
        remark: { maxLength: maxLength(300) },
        link: { required: requiredIf(() => !this.linkFileList.length) }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['yearList', 'departmentList', 'inculcationLevelList']),

    currentWork() {
      return this.incalculation
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.incalculation.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.incalculation[key])
      })

      Object.keys(this.$v.incalculation.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.incalculation.commonWork[key])
      })

      return allErrors
    }
  },

  methods: {
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    updateIncalculation() {
      this.$emit('update:selected-item', new Incalculation(this.incalculation.getForAPI()))
      this.updateInvalid()
    },

    uploadFiles(files) {
      this.$emit('uploadLinkFiles', files)
      this.updateIncalculation()
    },

    removeFile(index) {
      this.$emit('removeLinkFile', index)
      this.updateIncalculation()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.incalculation = new Incalculation(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
